/**
 * TacticalRuleDetail — expanded view of a single rule. Renders the
 * full rationale (markdown-friendly plain text), the supporting
 * episode count, PDO concept chips, and lifecycle metadata
 * (proposed_at, activated_at, last_validated_at, archived_at).
 *
 * Used inline beneath a row of cards when the user clicks one. Not a
 * modal — keeps the rule grid scrollable and lets the user open
 * multiple details at once for comparison.
 */

import { clsx } from 'clsx';
import {
  Calendar,
  ShieldCheck,
  Archive,
  X,
  Hash,
  Users,
  Layers,
} from 'lucide-react';
import { type CreativeMemoryRule } from '@/services/api';
import { ConfidenceMeter } from './ConfidenceMeter';
import { PdoConceptChips } from './PdoConceptChips';

interface TacticalRuleDetailProps {
  rule: CreativeMemoryRule;
  onClose: () => void;
}

const formatDate = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleString() : '—';

export function TacticalRuleDetail({ rule, onClose }: TacticalRuleDetailProps) {
  const isDo = rule.polarity === 'DO';

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-5">
      <header className="flex items-start gap-3 pb-3 border-b border-slate-200">
        <span
          className={clsx(
            'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide shrink-0',
            isDo ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700',
          )}
        >
          {rule.polarity}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-slate-800">
            {rule.name}
          </h3>
          <p className="mt-0.5 text-xs font-mono text-slate-500">
            {rule.rule_id}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
          aria-label="Close detail"
        >
          <X className="w-4 h-4" />
        </button>
      </header>

      {/* Confidence + episode count summary */}
      <section className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">
            Confidence
          </p>
          <div className="mt-1.5">
            <ConfidenceMeter value={rule.confidence} size="md" />
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">
            Evidence
          </p>
          <p className="mt-1.5 flex items-center gap-2 text-sm text-slate-700">
            <Hash className="w-3.5 h-3.5 text-slate-400" />
            {rule.episode_count} supporting episodes
          </p>
        </div>
      </section>

      {/* Rationale */}
      {rule.rationale && (
        <section className="mt-4">
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">
            Rationale
          </p>
          <p className="mt-1.5 whitespace-pre-line text-sm text-slate-700 leading-relaxed">
            {rule.rationale}
          </p>
        </section>
      )}

      {/* PDO concepts */}
      {rule.pdo_concept_refs.length > 0 && (
        <section className="mt-4">
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">
            PDO concepts grounded
          </p>
          <div className="mt-1.5">
            <PdoConceptChips
              conceptRefs={rule.pdo_concept_refs}
              conceptIds={rule.pdo_concept_ids}
              size="md"
            />
          </div>
        </section>
      )}

      {/* Scope */}
      {(rule.persona_ids.length > 0 ||
        rule.asset_types.length > 0 ||
        rule.funnel_stages.length > 0) && (
        <section className="mt-4">
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">
            Scope
          </p>
          <div className="mt-1.5 flex flex-wrap gap-3 text-xs text-slate-600">
            {rule.persona_ids.length > 0 && (
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3 text-slate-400" />
                {rule.persona_ids.join(', ')}
              </span>
            )}
            {rule.asset_types.length > 0 && (
              <span className="flex items-center gap-1">
                <Layers className="w-3 h-3 text-slate-400" />
                {rule.asset_types.join(', ')}
              </span>
            )}
            {rule.funnel_stages.length > 0 && (
              <span className="flex items-center gap-1">
                funnel: {rule.funnel_stages.join(', ')}
              </span>
            )}
          </div>
        </section>
      )}

      {/* Lifecycle */}
      <section className="mt-4 grid grid-cols-2 gap-3 text-[11px] text-slate-500 md:grid-cols-4">
        <Lifecycle
          icon={<Calendar className="w-3 h-3" />}
          label="Proposed"
          value={formatDate(rule.proposed_at)}
        />
        <Lifecycle
          icon={<Calendar className="w-3 h-3" />}
          label="Activated"
          value={formatDate(rule.activated_at)}
        />
        <Lifecycle
          icon={<ShieldCheck className="w-3 h-3" />}
          label="Last validated"
          value={formatDate(rule.last_validated_at)}
        />
        {rule.archived_at && (
          <Lifecycle
            icon={<Archive className="w-3 h-3" />}
            label="Archived"
            value={formatDate(rule.archived_at)}
          />
        )}
      </section>
    </div>
  );
}

interface LifecycleProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function Lifecycle({ icon, label, value }: LifecycleProps) {
  return (
    <div className="rounded border border-slate-200 bg-white px-2 py-1.5">
      <div className="flex items-center gap-1 text-slate-400">
        {icon}
        <span className="uppercase tracking-wide text-[9px]">{label}</span>
      </div>
      <p className="mt-0.5 text-slate-700 text-[11px]">{value}</p>
    </div>
  );
}
